import styled from 'styled-components'
import dayjs from 'dayjs'
import { useHistory, Link } from 'react-router-dom'
import { truncate, getTicketLinkedResourcePath, DATE_FORMAT } from '../../utils'
import ScreenLoader from '../ScreenLoader/ScreenLoader'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'

const CellStatusToBackgroundMap = p => {
  if (!p.status) return p.status;
  switch (p.status) {
    case 'Awaiting Reply':
      return 'var(--color-warning)';
    case 'Open':
      return 'var(--color-primary)';
    case 'Recommendation Created':
      return 'var(--color-secondary)';
    case 'Moved to Team Support Board':
      return 'var(--color-secondary)';
    default:
      return 'var(--color-text-secondary)'
  }
}

const StatusCellIconMap = status => ['Recommendation Created', 'Moved to Team Support Board'].includes(status)


const Table = styled.table`
  width: 100%;
  color: var(--color-text-primary);
  font-size: 12px;
  text-align: left;
  border: 1px solid var(--color-bg-sidebar);
  border-spacing: 0;
  background-color: var(--color-bg-card);
`

const Head = styled.th`
  min-height: 45px;
  padding: 10px;
  text-transform: capitalize;

  &.hide-mobile {
    @media only screen and (max-width: 920px) {
      display: none;
    }
  }
`

const Header = styled.tr`
  background-color: var(--color-bg-sidebar);
  color: var(--color-text-primary);
`

const Ticket = styled.tr`
  cursor: pointer;

  &:nth-child(even) td:not(:last-child) {
    background-color: rgba(42, 42, 42, 0.5);
  }

  &:hover td:not(:last-child) {
    background-color: var(--color-bg-sidebar);
    transition: background-color 0.2s ease;
  }
`

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
  gap: 10px;

  height: 45px;
  width: 100%;
  margin: -20px 0;

  text-decoration: none;
  color: currentColor;

  span {
    flex: 1;
  }
`

const Cell = styled.td`
  height: 45px;
  padding: 10px;
  border-bottom: 1px solid var(--color-bg-sidebar);
  transition: background-color 0.2s ease;

  &::first-letter {
    text-transform: capitalize;
  }

  &:nth-child(1) {
    width: 60px;
  }

  &:nth-child(3) {
    width: 230px;
  }

  &.hide-mobile {
    @media only screen and (max-width: 920px) {
      display: none;
    }
  }
`

const StatusCell = styled.td`
  height: 45px;
  padding: 10px;
  border-bottom: 1px solid var(--color-bg-sidebar);
  width: 130px;
  text-align: center;
  color: var(--color-bg-main);
  font-weight: bold;
  background-color: ${CellStatusToBackgroundMap};
  position: relative;
  z-index: 1;
  transition: background-color 0.2s ease;

  /* Prevent row hover from affecting this cell */
  &&& {
    background-color: ${CellStatusToBackgroundMap};
  }

  a {
    color: var(--color-bg-main);
    transition: color 0.2s ease;
  }

  &&&:hover {
    background-color: var(--color-sidebar-hover) !important;

    a {
      color: white;
    }
  }
`

const Fallback = styled.p`
  font-size: 14px;
  color: #a5acc2;
  text-align: center;
  margin-top: 22px;
`

const ErrorMsg = styled.p`
  color: red;
`

const SupportCasesTable = ({ tickets = [], loading, error }) => {
  const history = useHistory()

  if (loading) return <ScreenLoader />
  if (error) return <ErrorMsg>{error.message}</ErrorMsg>

  return (
    <>
      <Table>
        <thead>
          <Header>
            <Head>#</Head>
            <Head>Subject</Head>
            <Head className='hide-mobile'>Last Updated</Head>
            <Head>Status</Head>
          </Header>
        </thead>
        <tbody>
          {tickets.map(ticket => {
            const resourcePath = getTicketLinkedResourcePath(ticket.linkedTo)
            const ticketPath = `/support-cases/${ticket.id}`

            return (
              <Ticket onClick={() => history.push(ticketPath)} key={ticket.id}>
                <Cell>{ticket.id}</Cell>
                <Cell><StyledLink to={ticketPath}>{truncate(ticket.subject)}</StyledLink></Cell>
                <Cell className='hide-mobile'>{dayjs(ticket.updated).format(DATE_FORMAT)}</Cell>
                <StatusCell
                  status={ticket.status}
                  onClick={event => event.stopPropagation()}
                >
                  <StyledLink style={{ justifyContent: 'center' }} to={resourcePath || ticketPath}>
                    <span>{ticket.status}</span>{StatusCellIconMap(ticket.status) && <FontAwesomeIcon icon={faExternalLinkAlt} size='lg'/>}
                  </StyledLink>
                </StatusCell>
              </Ticket>
            )
          })}
        </tbody>
      </Table>
      {tickets.length < 1 && <Fallback>No cases found!</Fallback>}
    </>
  )
}

export default SupportCasesTable
